// Minecraft utility functions for skin fetching and item textures
import type { CSSProperties } from 'react';

export interface PlayerSkinInfo {
  skinUrl: string | null;
  capeUrl: string | null;
  model: 'slim' | 'classic';
  username: string | null;
}

// Fetch player skin info via our API route (to avoid CORS issues with Mojang API)
export async function fetchPlayerSkin(uuid: string): Promise<PlayerSkinInfo> {
  const defaultResult: PlayerSkinInfo = {
    skinUrl: null,
    capeUrl: null,
    model: 'classic',
    username: null
  };

  try {
    // Use our API route to fetch from Mojang (avoids CORS)
    const response = await fetch(`/api/player-skin/${uuid}`);

    if (!response.ok) {
      return defaultResult;
    }

    const data: PlayerSkinInfo = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to fetch player skin:', error);
    return defaultResult;
  }
}

// Default Steve skin URL (for fallback)
export const DEFAULT_SKIN_URL = 'https://textures.minecraft.net/texture/31f477eb1a7beee631c2ca64d06f8f68fa93a3386d04452ab27f43acdf1b60cb';

// ============================================
// TEXTURE FALLBACK RESOLUTION
// ============================================

const ITEM_DIR = '/minecraft-assets/textures/item/';
const BLOCK_DIR = '/minecraft-assets/textures/block/';

const WOOD_TYPES = ['oak', 'spruce', 'birch', 'jungle', 'acacia', 'dark_oak', 'mangrove', 'cherry', 'bamboo', 'crimson', 'warped', 'pale_oak'];
const MC_COLORS = ['white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray', 'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black'];

// Suffixes for wood items that should use the plank texture (no dedicated textures)
const WOOD_PLANK_SUFFIXES = ['_button', '_fence', '_fence_gate', '_slab', '_stairs', '_pressure_plate', '_planks'];

// Pre-build sets for fast lookup
const woodPlankItems = new Set<string>();
const woodLogItems = new Map<string, string>();
const woodItemTextures = new Map<string, string>(); // items with dedicated item/ textures
const colorBlockItems = new Set<string>();
const colorDerivedItems = new Map<string, string>();

for (const w of WOOD_TYPES) {
  for (const s of WOOD_PLANK_SUFFIXES) woodPlankItems.add(w + s);
  woodLogItems.set(w + '_log', w + '_log');
  woodLogItems.set(w + '_wood', w + '_log');
  woodLogItems.set('stripped_' + w + '_log', 'stripped_' + w + '_log');
  woodLogItems.set('stripped_' + w + '_wood', 'stripped_' + w + '_log');
  woodLogItems.set(w + '_trapdoor', w + '_trapdoor');
  woodLogItems.set(w + '_leaves', w + '_leaves');
  woodLogItems.set(w + '_sapling', w + '_sapling');
  woodLogItems.set(w + '_shelf', w + '_shelf');
  // Signs, hanging signs, and doors have dedicated item textures
  woodItemTextures.set(w + '_sign', w + '_sign');
  woodItemTextures.set(w + '_hanging_sign', w + '_hanging_sign');
  woodItemTextures.set(w + '_wall_sign', w + '_sign');
  woodItemTextures.set(w + '_wall_hanging_sign', w + '_hanging_sign');
  woodItemTextures.set(w + '_door', w + '_door');
}

for (const c of MC_COLORS) {
  for (const suffix of ['_wool', '_concrete', '_concrete_powder', '_terracotta', '_glazed_terracotta', '_stained_glass']) {
    colorBlockItems.add(c + suffix);
  }
  colorDerivedItems.set(c + '_stained_glass_pane', c + '_stained_glass');
  colorDerivedItems.set(c + '_carpet', c + '_wool');
  colorDerivedItems.set(c + '_bed', c + '_wool');
  colorDerivedItems.set(c + '_banner', c + '_wool');
  colorDerivedItems.set(c + '_wall_banner', c + '_wool');
}

// Stone/brick slab/stairs/wall → base block texture
const STONE_BASE: Record<string, string> = {
  'stone_slab': 'stone', 'stone_stairs': 'stone', 'stone_button': 'stone', 'stone_pressure_plate': 'stone',
  'cobblestone_slab': 'cobblestone', 'cobblestone_stairs': 'cobblestone', 'cobblestone_wall': 'cobblestone',
  'mossy_cobblestone_slab': 'mossy_cobblestone', 'mossy_cobblestone_stairs': 'mossy_cobblestone', 'mossy_cobblestone_wall': 'mossy_cobblestone',
  'stone_brick_slab': 'stone_bricks', 'stone_brick_stairs': 'stone_bricks', 'stone_brick_wall': 'stone_bricks',
  'mossy_stone_brick_slab': 'mossy_stone_bricks', 'mossy_stone_brick_stairs': 'mossy_stone_bricks', 'mossy_stone_brick_wall': 'mossy_stone_bricks',
  'brick_slab': 'bricks', 'brick_stairs': 'bricks', 'brick_wall': 'bricks',
  'nether_brick_slab': 'nether_bricks', 'nether_brick_stairs': 'nether_bricks', 'nether_brick_wall': 'nether_bricks', 'nether_brick_fence': 'nether_bricks',
  'red_nether_brick_slab': 'red_nether_bricks', 'red_nether_brick_stairs': 'red_nether_bricks', 'red_nether_brick_wall': 'red_nether_bricks',
  'sandstone_slab': 'sandstone', 'sandstone_stairs': 'sandstone', 'sandstone_wall': 'sandstone',
  'red_sandstone_slab': 'red_sandstone', 'red_sandstone_stairs': 'red_sandstone', 'red_sandstone_wall': 'red_sandstone',
  'smooth_stone_slab': 'smooth_stone',
  'smooth_sandstone_slab': 'sandstone_top', 'smooth_sandstone_stairs': 'sandstone_top',
  'smooth_red_sandstone_slab': 'red_sandstone_top', 'smooth_red_sandstone_stairs': 'red_sandstone_top',
  'prismarine_slab': 'prismarine', 'prismarine_stairs': 'prismarine', 'prismarine_wall': 'prismarine',
  'prismarine_brick_slab': 'prismarine_bricks', 'prismarine_brick_stairs': 'prismarine_bricks',
  'dark_prismarine_slab': 'dark_prismarine', 'dark_prismarine_stairs': 'dark_prismarine',
  'end_stone_brick_slab': 'end_stone_bricks', 'end_stone_brick_stairs': 'end_stone_bricks', 'end_stone_brick_wall': 'end_stone_bricks',
  'quartz_slab': 'quartz_block_side', 'quartz_stairs': 'quartz_block_side',
  'smooth_quartz_slab': 'quartz_block_bottom', 'smooth_quartz_stairs': 'quartz_block_bottom',
  'andesite_slab': 'andesite', 'andesite_stairs': 'andesite', 'andesite_wall': 'andesite',
  'polished_andesite_slab': 'polished_andesite', 'polished_andesite_stairs': 'polished_andesite',
  'diorite_slab': 'diorite', 'diorite_stairs': 'diorite', 'diorite_wall': 'diorite',
  'polished_diorite_slab': 'polished_diorite', 'polished_diorite_stairs': 'polished_diorite',
  'granite_slab': 'granite', 'granite_stairs': 'granite', 'granite_wall': 'granite',
  'polished_granite_slab': 'polished_granite', 'polished_granite_stairs': 'polished_granite',
  'deepslate_brick_slab': 'deepslate_bricks', 'deepslate_brick_stairs': 'deepslate_bricks', 'deepslate_brick_wall': 'deepslate_bricks',
  'deepslate_tile_slab': 'deepslate_tiles', 'deepslate_tile_stairs': 'deepslate_tiles', 'deepslate_tile_wall': 'deepslate_tiles',
  'cobbled_deepslate_slab': 'cobbled_deepslate', 'cobbled_deepslate_stairs': 'cobbled_deepslate', 'cobbled_deepslate_wall': 'cobbled_deepslate',
  'polished_deepslate_slab': 'polished_deepslate', 'polished_deepslate_stairs': 'polished_deepslate', 'polished_deepslate_wall': 'polished_deepslate',
  'blackstone_slab': 'blackstone', 'blackstone_stairs': 'blackstone', 'blackstone_wall': 'blackstone',
  'polished_blackstone_slab': 'polished_blackstone', 'polished_blackstone_stairs': 'polished_blackstone', 'polished_blackstone_wall': 'polished_blackstone',
  'polished_blackstone_brick_slab': 'polished_blackstone_bricks', 'polished_blackstone_brick_stairs': 'polished_blackstone_bricks', 'polished_blackstone_brick_wall': 'polished_blackstone_bricks',
  'polished_blackstone_button': 'polished_blackstone', 'polished_blackstone_pressure_plate': 'polished_blackstone',
  'mud_brick_slab': 'mud_bricks', 'mud_brick_stairs': 'mud_bricks', 'mud_brick_wall': 'mud_bricks',
  'tuff_slab': 'tuff', 'tuff_stairs': 'tuff', 'tuff_wall': 'tuff',
  'tuff_brick_slab': 'tuff_bricks', 'tuff_brick_stairs': 'tuff_bricks', 'tuff_brick_wall': 'tuff_bricks',
  'polished_tuff_slab': 'polished_tuff', 'polished_tuff_stairs': 'polished_tuff', 'polished_tuff_wall': 'polished_tuff',
  'resin_brick_slab': 'resin_bricks', 'resin_brick_stairs': 'resin_bricks', 'resin_brick_wall': 'resin_bricks',
  'purpur_slab': 'purpur_block', 'purpur_stairs': 'purpur_block',
  'cut_copper_slab': 'cut_copper', 'cut_copper_stairs': 'cut_copper',
  'exposed_cut_copper_slab': 'exposed_cut_copper', 'exposed_cut_copper_stairs': 'exposed_cut_copper',
  'weathered_cut_copper_slab': 'weathered_cut_copper', 'weathered_cut_copper_stairs': 'weathered_cut_copper',
  'oxidized_cut_copper_slab': 'oxidized_cut_copper', 'oxidized_cut_copper_stairs': 'oxidized_cut_copper',
  'petrified_oak_slab': 'oak_planks',
};

// Specific one-off mappings for common items
const SPECIFIC_FALLBACKS: Record<string, string> = {
  'terracotta': 'terracotta',
  'glass': 'glass',
  'glass_pane': 'glass',
  'tinted_glass': 'tinted_glass',
  'iron_bars': 'iron_bars',
  'iron_block': 'iron_block',
  'gold_block': 'gold_block',
  'diamond_block': 'diamond_block',
  'emerald_block': 'emerald_block',
  'lapis_block': 'lapis_block',
  'netherite_block': 'netherite_block',
  'coal_block': 'coal_block',
  'copper_block': 'copper_block',
  'raw_iron_block': 'raw_iron_block',
  'raw_copper_block': 'raw_copper_block',
  'raw_gold_block': 'raw_gold_block',
  'redstone_block': 'redstone_block',
  'slime_block': 'slime_block',
  'glowstone': 'glowstone',
  'cobblestone': 'cobblestone',
  'mossy_cobblestone': 'mossy_cobblestone',
  'stone': 'stone',
  'smooth_stone': 'smooth_stone',
  'stone_bricks': 'stone_bricks',
  'mossy_stone_bricks': 'mossy_stone_bricks',
  'cracked_stone_bricks': 'cracked_stone_bricks',
  'chiseled_stone_bricks': 'chiseled_stone_bricks',
  'bricks': 'bricks',
  'nether_bricks': 'nether_bricks',
  'red_nether_bricks': 'red_nether_bricks',
  'cracked_nether_bricks': 'cracked_nether_bricks',
  'chiseled_nether_bricks': 'chiseled_nether_bricks',
  'end_stone': 'end_stone',
  'end_stone_bricks': 'end_stone_bricks',
  'sandstone': 'sandstone',
  'red_sandstone': 'red_sandstone',
  'prismarine': 'prismarine',
  'prismarine_bricks': 'prismarine_bricks',
  'dark_prismarine': 'dark_prismarine',
  'mud_bricks': 'mud_bricks',
  'deepslate': 'deepslate',
  'cobbled_deepslate': 'cobbled_deepslate',
  'polished_deepslate': 'polished_deepslate',
  'deepslate_bricks': 'deepslate_bricks',
  'deepslate_tiles': 'deepslate_tiles',
  'blackstone': 'blackstone',
  'polished_blackstone': 'polished_blackstone',
  'polished_blackstone_bricks': 'polished_blackstone_bricks',
  'tuff': 'tuff',
  'tuff_bricks': 'tuff_bricks',
  'polished_tuff': 'polished_tuff',
  'resin_bricks': 'resin_bricks',
  'andesite': 'andesite',
  'polished_andesite': 'polished_andesite',
  'diorite': 'diorite',
  'polished_diorite': 'polished_diorite',
  'granite': 'granite',
  'polished_granite': 'polished_granite',
  'purpur_block': 'purpur_block',
  'purpur_pillar': 'purpur_pillar',
  'quartz_block': 'quartz_block_side',
  'quartz_bricks': 'quartz_bricks',
  'quartz_pillar': 'quartz_pillar',
  'smooth_quartz': 'quartz_block_bottom',
  'smooth_sandstone': 'sandstone_top',
  'smooth_red_sandstone': 'red_sandstone_top',
  'cut_sandstone': 'cut_sandstone',
  'cut_red_sandstone': 'cut_red_sandstone',
  'redstone_lamp': 'redstone_lamp',
  'redstone_torch': 'redstone_torch',
  'soul_sand': 'soul_sand',
  'bone_block': 'bone_block_side',
  'nether_wart_block': 'nether_wart_block',
  'warped_wart_block': 'warped_wart_block',
  'amethyst_block': 'amethyst_block',
  'heavy_weighted_pressure_plate': 'iron_block',
  'light_weighted_pressure_plate': 'gold_block',
  'exposed_copper': 'exposed_copper',
  'weathered_copper': 'weathered_copper',
  'oxidized_copper': 'oxidized_copper',
  'cut_copper': 'cut_copper',
  'exposed_cut_copper': 'exposed_cut_copper',
  'weathered_cut_copper': 'weathered_cut_copper',
  'oxidized_cut_copper': 'oxidized_cut_copper',
  'iron_trapdoor': 'iron_trapdoor',
  'copper_trapdoor': 'copper_trapdoor',
  'exposed_copper_trapdoor': 'exposed_copper_trapdoor',
  'weathered_copper_trapdoor': 'weathered_copper_trapdoor',
  'oxidized_copper_trapdoor': 'oxidized_copper_trapdoor',
};

// Items with dedicated item/ textures that aren't wood-based
const SPECIFIC_ITEM_FALLBACKS: Record<string, string> = {
  'iron_door': 'iron_door',
  'copper_door': 'copper_door',
  'exposed_copper_door': 'exposed_copper_door',
  'weathered_copper_door': 'weathered_copper_door',
  'oxidized_copper_door': 'oxidized_copper_door',
  'crossbow': 'crossbow_standby',
  'compass': 'compass_16',
  'recovery_compass': 'recovery_compass_16',
};

// Items that have no flat item texture — use rendered 3D textures instead
const RENDERED_TEXTURE_FALLBACKS: Record<string, string> = {
  'shield': 'shield',
  'skeleton_skull': 'skeleton_skull',
  'wither_skeleton_skull': 'wither_skeleton_skull',
  'zombie_head': 'zombie_head',
  'creeper_head': 'creeper_head',
  'dragon_head': 'dragon_head',
  'piglin_head': 'piglin_head',
  'player_head': 'player_head',
  'carved_pumpkin': 'carved_pumpkin',
};

/**
 * Resolve a fallback texture path for items/blocks without dedicated textures.
 * Returns a block texture path or null if no known fallback exists.
 */
function resolveTextureFallback(id: string): string | null {
  // 1. Items with dedicated item textures (signs, hanging signs, doors)
  const woodItemTex = woodItemTextures.get(id);
  if (woodItemTex) return `${ITEM_DIR}${woodItemTex}.png`;
  const specificItemTex = SPECIFIC_ITEM_FALLBACKS[id];
  if (specificItemTex) return `${ITEM_DIR}${specificItemTex}.png`;
  const renderedTex = RENDERED_TEXTURE_FALLBACKS[id];
  // These are all vanilla ids; use the collision-proof namespaced render so a mod
  // sharing the id can't clobber the icon (see docs/codebase-map.md).
  if (renderedTex) return `/textures/rendered/minecraft/${renderedTex}.png`;

  // 2. Wood items → plank texture (buttons, fences, slabs, etc.)
  if (woodPlankItems.has(id)) {
    const wood = WOOD_TYPES.find(w => id.startsWith(w + '_'));
    if (wood) return `${BLOCK_DIR}${wood}_planks.png`;
  }

  // 3. Wood logs, trapdoors, leaves, etc. → specific block texture
  const logMapping = woodLogItems.get(id);
  if (logMapping) return `${BLOCK_DIR}${logMapping}.png`;

  // 3. Colored block items → same name in block dir
  if (colorBlockItems.has(id)) return `${BLOCK_DIR}${id}.png`;

  // 4. Colored derived items (carpet→wool, stained_glass_pane→stained_glass, etc.)
  const colorDerived = colorDerivedItems.get(id);
  if (colorDerived) return `${BLOCK_DIR}${colorDerived}.png`;

  // 5. Stone/brick slabs, stairs, walls → base block
  if (STONE_BASE[id]) return `${BLOCK_DIR}${STONE_BASE[id]}.png`;

  // 6. Specific one-off mappings
  if (SPECIFIC_FALLBACKS[id]) return `${BLOCK_DIR}${SPECIFIC_FALLBACKS[id]}.png`;

  // 7. Ores → block texture
  if (id.endsWith('_ore')) return `${BLOCK_DIR}${id}.png`;

  // 8. Waxed copper variants → unwaxed equivalent
  if (id.startsWith('waxed_')) {
    const unwaxed = id.replace('waxed_', '');
    return resolveTextureFallback(unwaxed) ?? `${BLOCK_DIR}${unwaxed}.png`;
  }

  return null;
}

// ============================================
// TEXTURE PATH FUNCTIONS
// ============================================

// Ids with no inventory icon at all. Surfaces show their "no icon" placeholder for
// these instead of requesting an image: the old air texturePath pointed at a
// flat rendered/air.png that is a blue-noise dump artifact, and the others 404.
// end_gateway is here too: its data points at item/end_gateway.png, which is absent in ALL 32
// version folders. end_portal is NOT — some versions do ship a stand-in render for it.
const ICONLESS_IDS = new Set(['air', 'cave_air', 'void_air', 'end_gateway']);

export function isIconlessId(itemId: string): boolean {
  return typeof itemId === 'string' && ICONLESS_IDS.has(itemId.replace('minecraft:', ''));
}

// Stained/tinted glass icons are the game's own flat sprite, a uniform tint at
// ~40-65% alpha. On our dark slots the dark colors (black, gray, brown, red,
// blue, tinted) all but vanish, so they read as empty squares. (The 3D renders
// are no alternative: the dump's magenta chroma key bled into every translucent
// block, e.g. rendered/minecraft/blue_stained_glass.png is purple.) Draw these
// over a light/dark checkerboard — the universal "see-through" cue — so every
// color contrasts with one of the two squares.
// `glass` covers glass_pane too (same sprite) and is the WORST case: measured 75% fully transparent
// vs 0% for the stained colors. frogspawn is here for the same reason (68% transparent, mean alpha
// 0.22) — without it the eggs are invisible on a dark slot.
const TRANSLUCENT_ICON_RE = /\/minecraft-assets\/textures\/block\/(?:glass|[a-z_]+_stained_glass|tinted_glass|frogspawn)\.png$/;

export function isTranslucentIconPath(texturePath: string | undefined | null): boolean {
  return typeof texturePath === 'string' && TRANSLUCENT_ICON_RE.test(texturePath);
}

/** Inline style for an icon <img>: a 4x4 checkerboard behind translucent sprites, else undefined. */
export function translucentIconStyle(texturePath: string | undefined | null): CSSProperties | undefined {
  if (!isTranslucentIconPath(texturePath)) return undefined;
  return {
    backgroundImage: 'repeating-conic-gradient(#a1a1aa 0% 25%, #52525b 0% 50%)',
    backgroundSize: '50% 50%',
  };
}

// Map Minecraft item ID to texture filename
// Optional version parameter for future per-version texture overrides
export function getItemTexturePath(itemId: string, _version?: string): string {
  if (typeof itemId !== 'string') return `${ITEM_DIR}unknown.png`;
  const id = itemId.replace('minecraft:', '');

  // Fallback resolver for items without a rendered texture
  const fallback = resolveTextureFallback(id);
  if (fallback) return fallback;

  return `${ITEM_DIR}${id}.png`;
}

// Blocks that don't have visible textures (invisible/technical blocks)
const INVISIBLE_BLOCKS = new Set([
  'air',
  'cave_air',
  'void_air',
  'light',
  'barrier',
  'structure_void',
]);

// Map Minecraft block item ID to block texture (for blocks that are items)
// Optional version parameter for future per-version texture overrides
export function getBlockTexturePath(itemId: string, _version?: string): string {
  if (typeof itemId !== 'string') return '';
  const id = itemId.replace('minecraft:', '');

  if (INVISIBLE_BLOCKS.has(id)) {
    return '';
  }

  // Fallback resolver for blocks without a rendered texture
  const fallback = resolveTextureFallback(id);
  if (fallback) return fallback;

  return `${BLOCK_DIR}${id}.png`;
}

const COMPACT_COUNT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

/**
 * A stack count as it fits in a 56px inventory slot: exact up to 9,999, compact above ("12K", "2.1B").
 * Counts are a signed int in NBT, so overstacked items (commands, editors) reach 2,147,483,647, which
 * overflowed the slot. The tooltip and aria-label always carry the exact count.
 */
export function formatSlotCount(count: number): string {
  return Math.abs(count) <= 9999 ? String(count) : COMPACT_COUNT.format(count);
}

// Get item display name from ID
export function getItemDisplayName(itemId: string): string {
  if (typeof itemId !== 'string') return 'Unknown';
  // Remove namespace
  const id = itemId.replace('minecraft:', '');

  // Convert snake_case to Title Case
  return id
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Armor slot names
export const ARMOR_SLOT_NAMES = ['Boots', 'Leggings', 'Chestplate', 'Helmet'] as const;

// Inventory slot layout
export const HOTBAR_SLOTS = [0, 1, 2, 3, 4, 5, 6, 7, 8];
export const MAIN_INVENTORY_ROWS = [
  [9, 10, 11, 12, 13, 14, 15, 16, 17],   // Row 1
  [18, 19, 20, 21, 22, 23, 24, 25, 26],  // Row 2
  [27, 28, 29, 30, 31, 32, 33, 34, 35],  // Row 3
];

// Armor slot types and validation
export type ArmorSlotType = 'boots' | 'leggings' | 'chestplate' | 'helmet';

export const ARMOR_SLOT_TYPES: ArmorSlotType[] = ['boots', 'leggings', 'chestplate', 'helmet'];

// Check if an item is valid for a specific armor slot
export function isValidArmorForSlot(itemId: string, slotIndex: number): boolean {
  if (typeof itemId !== 'string') return false;
  const id = itemId.replace('minecraft:', '').toLowerCase();
  const slotType = ARMOR_SLOT_TYPES[slotIndex];

  // Check if item matches the slot type
  switch (slotType) {
    case 'boots':
      return id.endsWith('_boots');
    case 'leggings':
      return id.endsWith('_leggings');
    case 'chestplate':
      return id.endsWith('_chestplate') || id === 'elytra';
    case 'helmet':
      return id.endsWith('_helmet') || id === 'turtle_helmet' || id.endsWith('_head') || id.endsWith('_skull') || id === 'carved_pumpkin';
    default:
      return false;
  }
}

// Check if an item is any type of armor
export function isArmorItem(itemId: string): boolean {
  if (typeof itemId !== 'string') return false;
  const id = itemId.replace('minecraft:', '').toLowerCase();
  return (
    id.endsWith('_boots') ||
    id.endsWith('_leggings') ||
    id.endsWith('_chestplate') ||
    id.endsWith('_helmet') ||
    id === 'turtle_helmet' ||
    id === 'elytra' ||
    id.endsWith('_head') ||
    id.endsWith('_skull') ||
    id === 'carved_pumpkin'
  );
}

// Get which armor slot an item belongs to (returns -1 if not armor)
export function getArmorSlotForItem(itemId: string): number {
  if (typeof itemId !== 'string') return -1;
  const id = itemId.replace('minecraft:', '').toLowerCase();

  if (id.endsWith('_boots')) return 0;
  if (id.endsWith('_leggings')) return 1;
  if (id.endsWith('_chestplate') || id === 'elytra') return 2;
  if (id.endsWith('_helmet') || id === 'turtle_helmet' || id.endsWith('_head') || id.endsWith('_skull') || id === 'carved_pumpkin') return 3;

  return -1;
}
